import React from "react";
import { Box, Grid } from "@mui/material";
import {
  NotificationsActiveOutlined, ErrorOutlineOutlined, WarningAmberOutlined,
  InfoOutlined, CheckCircleOutlineOutlined, TimerOutlined,
} from "@mui/icons-material";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import DemoShell from "./DemoShell";
import { MetricCard, ChartCard, DarkTooltip, AlertRow } from "./demoComponents";
import { demo } from "./demoStyles";

// ── Mock data ────────────────────────────────────────────────────────────────
const alertsBySystem = [
  { system: "RMS",         critical: 4, warning: 12, info: 8  },
  { system: "Smart Meter", critical: 2, warning: 6,  info: 4  },
  { system: "Solar",       critical: 1, warning: 4,  info: 3  },
  { system: "Billing",     critical: 0, warning: 2,  info: 0  },
  { system: "Assets",      critical: 1, warning: 0,  info: 0  },
];

const trendData = Array.from({ length: 14 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - 13 + i);
  const label = `${d.getDate()} ${d.toLocaleString("en-IN", { month: "short" })}`;
  return {
    day: label,
    critical: Math.floor(Math.random() * 8)  + 2,
    warning:  Math.floor(Math.random() * 18) + 8,
    info:     Math.floor(Math.random() * 12) + 4,
  };
});

const recentAlerts = [
  { severity: "critical", system: "RMS · Pump SP-04618", message: "Pump offline — no heartbeat for 48 minutes", time: "2 min ago" },
  { severity: "critical", system: "RMS · Pump SP-03291", message: "Motor overcurrent detected (Phase 2)", time: "14 min ago" },
  { severity: "warning",  system: "Smart Meter · DL-00421", message: "Tamper event detected on meter enclosure", time: "28 min ago" },
  { severity: "warning",  system: "Solar · Plant GJ-08",    message: "String 4 output below expected by 22%", time: "41 min ago" },
  { severity: "warning",  system: "RMS · Pump UP-01042",    message: "Water level sensor returning null readings", time: "1 hr ago" },
  { severity: "info",     system: "Assets · INV-1182",      message: "Preventive maintenance due in 3 days", time: "2 hr ago" },
  { severity: "resolved", system: "RMS · Pump HR-00892",    message: "Low voltage fault cleared — power restored", time: "3 hr ago" },
  { severity: "resolved", system: "Smart Meter · UP-01184", message: "Communication timeout resolved automatically", time: "4 hr ago" },
];

const GRADIENT = "linear-gradient(135deg, #fb7185, #ef4444)";

export default function AlertsCenterPage() {
  return (
    <DemoShell title="Alerts Center" icon={NotificationsActiveOutlined} gradient={GRADIENT}>
      {/* ── KPI row ── */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          { icon: NotificationsActiveOutlined,   label: "Alerts Today",       value: "47",   sub: "Across all systems",          gradient: GRADIENT },
          { icon: ErrorOutlineOutlined,           label: "Critical",           value: "8",    trend: "-3 vs yesterday", trendUp: true,  gradient: "linear-gradient(135deg, #f87171, #ef4444)" },
          { icon: WarningAmberOutlined,           label: "Warnings",           value: "24",   trend: "+2 vs yesterday", trendUp: false, gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)" },
          { icon: InfoOutlined,                   label: "Informational",      value: "15",   sub: "Low priority",                gradient: "linear-gradient(135deg, #22d3ee, #3b82f6)" },
          { icon: CheckCircleOutlineOutlined,     label: "Resolved (7 days)",  value: "124",  trend: "+18% resolution rate", trendUp: true, gradient: "linear-gradient(135deg, #34d399, #22d3ee)" },
          { icon: TimerOutlined,                  label: "Avg Resolution",     value: "2.4",  unit: "hr", sub: "Mean time to resolve", gradient: "linear-gradient(135deg, #a78bfa, #6366f1)" },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <MetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts row ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {/* Alerts by system */}
        <Grid item xs={12} lg={5}>
          <ChartCard title="Alerts by System" subtitle="Today · Severity breakdown">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={alertsBySystem} layout="vertical"
                margin={{ top: 4, right: 20, bottom: 0, left: 10 }} barSize={14}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="system" tick={{ fill: demo.text, fontSize: 12 }} tickLine={false} axisLine={false} width={72} />
                <Tooltip content={<DarkTooltip unit=" alerts" />} />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12, textTransform: "capitalize" }}>{v}</span>} />
                <Bar dataKey="critical" stackId="a" fill="#f87171" radius={[0,0,0,0]} />
                <Bar dataKey="warning"  stackId="a" fill={demo.amber} radius={[0,0,0,0]} />
                <Bar dataKey="info"     stackId="a" fill={demo.accent} radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* 14-day trend */}
        <Grid item xs={12} lg={7}>
          <ChartCard title="14-Day Alert Trend" subtitle="Daily count by severity">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fill: demo.textFaint, fontSize: 9.5 }} tickLine={false} interval={3} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<DarkTooltip unit=" alerts" />} />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12, textTransform: "capitalize" }}>{v}</span>} />
                <Line type="monotone" dataKey="critical" stroke="#f87171"   strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="warning"  stroke={demo.amber} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="info"     stroke={demo.accent} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ── Recent alerts list ── */}
      <ChartCard title="Recent Alerts" subtitle="Live feed · All systems · Last 8 events">
        {recentAlerts.map((a, i) => (
          <AlertRow key={i} {...a} />
        ))}
      </ChartCard>
    </DemoShell>
  );
}
